import { useEffect, useState, type CSSProperties } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature, type Weekday } from '../../../Models/enums';
import type { StudentProfileModel } from '../../../Models/ReportModel';
import type { AvailabilityRangeModel } from '../../../Models/StudentModel';
import { ReportService } from '../../../Services/ReportService';
import { StudentService } from '../../../Services/StudentService';

/** Sunday first, matching the working week here. */
const WEEKDAYS: Weekday[] = [0, 1, 2, 3, 4, 5, 6];
import { formatDate } from '../../../Utils/DateUtil';
import { routes } from '../../../Utils/routes';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { PremiumGate } from '../Premium/PremiumGate';
import './StudentProfile.css';
import { contactDisplayName, contactDisplayRelation, studentDisplayName } from '../../../Utils/DisplayNameUtil';

/**
 * One student, on one page: their money, their rhythm, and how to reach them.
 *
 * Not the dashboard scaled down. A single student has no ranking and no
 * distribution across the roster, but they do have a gap since the last
 * lesson and a record of turning up, and those are what a tutor looks for
 * before deciding whether to chase.
 */
export function StudentProfile() {
  return (
    <PremiumGate feature={PremiumFeature.StudentProfile}>
      <StudentProfileInner />
    </PremiumGate>
  );
}

function StudentProfileInner() {
  const { tr, language } = useTranslation();
  const { name } = useParams();
  const [profile, setProfile] = useState<StudentProfileModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!name) return;
    setError(null);
    ReportService.studentProfile(name)
      .then(setProfile)
      .catch(failure => setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR'));
  }, [name]);

  if (error) return <ErrorState code={error} />;
  if (!profile) return <Spinner />;

  const { totals, rhythm } = profile;

  /**
   * Gone quiet: nothing booked, and the gap since the last lesson is well past
   * this student's own normal rhythm.
   *
   * Measured against their own average gap rather than a fixed number of days,
   * because a fortnightly student is not late at three weeks and a twice-weekly
   * one is. Three weeks is the floor, so a student who has had two lessons ever
   * does not get flagged for a quiet Tuesday.
   */
  const money = totals.collected + totals.owed + totals.upcoming;

  const quiet = rhythm.daysSinceLast !== null
    && !rhythm.nextLesson
    && rhythm.daysSinceLast >= Math.max(21, Math.round((rhythm.averageGapDays ?? 7) * 2.5));

  return (
    <section className="student-profile">
      <header className="student-profile-header">
        <h1 className="screen-title">{studentDisplayName(profile.student, language)}</h1>
        <Link className="ghost-button" to={routes.students}>{tr('back')}</Link>
      </header>

      {/* A student who has gone quiet is the one thing on this page that is
          not a figure to read but something to act on, so it sits above every
          figure that explains it. */}
      {quiet && (
        <p className="profile-quiet-warning" role="status">
          <span aria-hidden="true">!</span>
          {tr('studentQuietWarning', rhythm.daysSinceLast ?? 0)}
        </p>
      )}

      <div className="dashboard-figures">
        <article className="figure is-collected">
          <p className="figure-label">{tr('income')}</p>
          <MoneyText amount={totals.collected} className="figure-value" />
        </article>
        <article className="figure is-owed">
          <p className="figure-label">{tr('debt')}</p>
          <MoneyText amount={totals.owed} className="figure-value" />
        </article>
        <article className="figure is-scheduled">
          <p className="figure-label">{tr('scheduledIncome')}</p>
          <MoneyText amount={totals.upcoming} className="figure-value" />
        </article>
      </div>

      {/* The same three figures as a shape. A ratio is what a tutor is
          actually reading here, and a ratio is easier to see than to divide. */}
      {money > 0 && (
        <section className="dashboard-panel">
          <h2>{tr('paymentStatusChart')}</h2>
          <div className="profile-status">
            <div
              className="profile-donut"
              style={{
                '--paid-stop': `${(totals.collected / money) * 100}%`,
                '--owed-stop': `${((totals.collected + totals.owed) / money) * 100}%`
              } as CSSProperties}
            >
              <strong>{totals.lessonsDone}</strong>
              <span>{tr('lessonsDone')}</span>
            </div>
            <ul className="chart-legend">
              <li><i className="is-paid" />{tr('paid')} <b><MoneyText amount={totals.collected} /></b></li>
              <li><i className="is-owed" />{tr('debt')} <b><MoneyText amount={totals.owed} /></b></li>
              <li><i className="is-scheduled" />{tr('scheduled')} <b><MoneyText amount={totals.upcoming} /></b></li>
            </ul>
          </div>
        </section>
      )}

      <section className="dashboard-panel">
        <h2>{tr('rhythmTitle')}</h2>
        <ul className="reports-facts">
          <li>
            <span>{tr('lessonsDone')}</span>
            <bdi>{totals.lessonsDone}</bdi>
          </li>
          <li>
            <span>{tr('attendanceRate')}</span>
            <bdi>{totals.attendanceRate === null ? '·' : `${Math.round(totals.attendanceRate)}%`}</bdi>
          </li>
          <li>
            <span>{tr('daysSinceLast')}</span>
            <bdi>{rhythm.daysSinceLast ?? '·'}</bdi>
          </li>
          <li>
            <span>{tr('lessonsPerMonth')}</span>
            <bdi>{rhythm.lessonsPerMonth ?? '·'}</bdi>
          </li>
          <li>
            <span>{tr('freeCancels')}</span>
            <bdi>{totals.freeCancels}</bdi>
          </li>
          <li>
            <span>{tr('chargedCancels')}</span>
            <bdi>{totals.chargedCancels}</bdi>
          </li>
        </ul>
      </section>

      {rhythm.nextLesson && (
        <section className="dashboard-panel">
          <h2>{tr('nextLessonTitle')}</h2>
          <p className="dashboard-next">
            <bdi>{formatDate(rhythm.nextLesson.date, language)}</bdi>
            {rhythm.nextLesson.time && <bdi>{rhythm.nextLesson.time}</bdi>}
            <MoneyText amount={rhythm.nextLesson.amount} />
          </p>
        </section>
      )}

      <section className="dashboard-panel">
        <h2>{tr('monthlyTrend')}</h2>
        <ul className="profile-months">
          {profile.months.map(entry => (
            <li key={entry.month}>
              <span
                className="profile-month-bar"
                style={{ blockSize: `${monthHeight(entry.amount, profile)}%` }}
                aria-hidden="true"
              />
              <span className="profile-month-label"><bdi>{entry.month.slice(5)}</bdi></span>
              <span className="visually-hidden">
                {entry.month} <MoneyText amount={entry.amount} />
              </span>
            </li>
          ))}
        </ul>
      </section>

      {profile.slots.length > 0 && (
        <section className="dashboard-panel">
          <h2>{tr('recurringHours')}</h2>
          <ul className="profile-list">
            {profile.slots.map(slot => (
              <li key={slot.id}>
                <span>{tr(WEEKDAY_KEYS[slot.weekday])}</span>
                <bdi>{slot.time}</bdi>
                <bdi>{slot.durationMinutes} {tr('minutes')}</bdi>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Editable here rather than read-only, because this is where a tutor
          is when a student says which afternoons now work for them. */}
      <AvailabilityEditor
        student={profile.student.name}
        availability={profile.availability}
        onChange={availability => setProfile({ ...profile, availability })}
      />

      <section className="dashboard-panel">
        <h2>{tr('lessonsByWeekday')}</h2>
        <ul className="profile-weekdays">
          {WEEKDAYS.map(day => {
            const lessons = profile.byWeekday.find(entry => entry.weekday === day)?.lessons ?? 0;
            return (
              <li key={day}>
                <span
                  className="profile-weekday-bar"
                  style={{ blockSize: `${weekdayHeight(lessons, profile)}%` }}
                  aria-hidden="true"
                />
                <span className="profile-weekday-label">{tr(WEEKDAY_KEYS[day])}</span>
                <span className="visually-hidden">{tr(WEEKDAY_KEYS[day])} <bdi>{lessons}</bdi></span>
              </li>
            );
          })}
        </ul>
      </section>

      {profile.contacts.length > 0 && (
        <section className="dashboard-panel">
          <h2>{tr('contactsTitle')}</h2>
          <ul className="profile-list">
            {profile.contacts.map(contact => (
              <li key={contact.id}>
                <span>{contactDisplayName(contact, language)}</span>
                {contact.relation && <span className="dashboard-quiet">{contactDisplayRelation(contact, language)}</span>}
                {contact.phone && <bdi>{contact.phone}</bdi>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
}

function monthHeight(amount: number, profile: StudentProfileModel): number {
  const peak = Math.max(...profile.months.map(entry => entry.amount), 1);
  return Math.max(Math.round((amount / peak) * 100), 2);
}

function weekdayHeight(lessons: number, profile: StudentProfileModel): number {
  const peak = Math.max(...profile.byWeekday.map(entry => entry.lessons), 1);
  // Never zero: a day with no lessons still needs a baseline to sit on, or
  // the row of labels loses its alignment.
  return Math.max(Math.round((lessons / peak) * 100), 2);
}

/**
 * When this student can learn, edited in place.
 *
 * Availability is stored as a whole list rather than row by row, so every
 * change here is a PUT of the complete set. That is the server's shape, and
 * pretending otherwise in the client would mean maintaining a diff for no
 * benefit.
 */
function AvailabilityEditor({ student, availability, onChange }: {
  student: string;
  availability: AvailabilityRangeModel[];
  onChange: (availability: AvailabilityRangeModel[]) => void;
}) {
  const { tr } = useTranslation();
  const [weekday, setWeekday] = useState<Weekday>(0);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(next: AvailabilityRangeModel[]) {
    setBusy(true);
    try {
      onChange(await StudentService.setAvailability(student, next));
    } catch {
      // The list on screen is still the stored one; nothing changed.
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="dashboard-panel">
      <h2>{tr('availabilityTitle')}</h2>

      {availability.length > 0 && (
        <ul className="profile-list">
          {availability.map(range => (
            <li key={`${range.weekday}-${range.startTime}`}>
              <span>{tr(WEEKDAY_KEYS[range.weekday as Weekday])}</span>
              <bdi>{range.startTime}</bdi>
              <bdi>{range.endTime}</bdi>
              <button
                type="button"
                className="profile-range-remove"
                onClick={() => void save(availability.filter(item => item !== range))}
                disabled={busy}
                aria-label={tr('remove')}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="profile-availability-add">
        <label className="field">
          <span>{tr('weekday')}</span>
          <select className="text-input" value={weekday} onChange={event => setWeekday(Number(event.target.value) as Weekday)}>
            {WEEKDAYS.map(day => <option key={day} value={day}>{tr(WEEKDAY_KEYS[day])}</option>)}
          </select>
        </label>
        <label className="field">
          <span>{tr('fromHour')}</span>
          <input className="text-input" type="time" value={startTime} onChange={event => setStartTime(event.target.value)} />
        </label>
        <label className="field">
          <span>{tr('toHour')}</span>
          <input className="text-input" type="time" value={endTime} onChange={event => setEndTime(event.target.value)} />
        </label>
        <button
          type="button"
          className="ghost-button"
          disabled={busy || !startTime || !endTime || endTime <= startTime}
          onClick={() => void save([...availability, { weekday, startTime, endTime }])}
        >
          {tr('add')}
        </button>
      </div>
    </section>
  );
}
